import { create } from 'zustand';
import { useAuthStore } from '@/store/auth-store';
import { sessionScope } from '@/features/auth/session-scope';

/**
 * Global voice-session state.
 *
 * The in-room LiveKit connection must SURVIVE route navigation (an agent joins
 * a voice channel, then browses Inbox/CRM while still connected). React unmounts
 * anything inside the route tree on navigation, so the voice room is mounted
 * app-wide via `<VoiceDock/>` (in providers, outside routes) and driven by this
 * store — NOT rendered inside the team-chat panel.
 *
 *   - `channelId` set → a session exists (the dock renders it).
 *   - `phase: 'lobby'` → pre-join screen (device check) before connecting;
 *     `'live'` → connected room. `enter()` transitions lobby → live.
 *   - `minimized` → dock shows a floating pill instead of the full overlay;
 *     the LiveKit connection stays up either way (audio keeps flowing).
 *
 * Read non-React via `useVoiceSessionStore.getState()`. Cleared on logout.
 */
export interface VoiceDevicePrefs {
  micId?: string;
  cameraId?: string;
  speakerId?: string;
  /** Join with the mic muted? */
  joinMuted: boolean;
  /** Join with the camera on? (voice is audio-first, default off) */
  joinCamera: boolean;
}

const DEFAULT_PREFS: VoiceDevicePrefs = { joinMuted: false, joinCamera: false };

interface VoiceSessionState {
  revision: number;
  channelId: string | null;
  channelName?: string;
  phase: 'lobby' | 'live';
  minimized: boolean;
  devicePrefs: VoiceDevicePrefs;
  /** Open the pre-join lobby for a voice channel. */
  join: (channelId: string, channelName?: string) => void;
  /** Lobby → live: connect with the chosen device prefs. */
  enter: (prefs: VoiceDevicePrefs) => void;
  /** Leave the session entirely (the dock unmounts the room → disconnect). */
  leave: () => void;
  setMinimized: (minimized: boolean) => void;
  clear: () => void;
}

export const useVoiceSessionStore = create<VoiceSessionState>((set) => ({
  revision:0,
  channelId: null,
  channelName: undefined,
  phase: 'lobby',
  minimized: false,
  devicePrefs: DEFAULT_PREFS,
  join: (channelId, channelName) =>
    set(s=>({ revision:s.revision+1,channelId, channelName, phase: 'lobby', minimized: false, devicePrefs: DEFAULT_PREFS })),
  enter: (prefs) => set(s=>({ revision:s.revision+1,phase: 'live', devicePrefs: prefs })),
  leave: () =>
    set(s=>({ revision:s.revision+1,channelId: null, channelName: undefined, phase: 'lobby', minimized: false, devicePrefs: DEFAULT_PREFS })),
  setMinimized: (minimized) => set({ minimized }),
  clear: () =>
    set(s=>({ revision:s.revision+1,channelId: null, channelName: undefined, phase: 'lobby', minimized: false, devicePrefs: DEFAULT_PREFS })),
}));

useAuthStore.subscribe((next,previous)=>{if(sessionScope(next)!==sessionScope(previous))useVoiceSessionStore.getState().clear();});
